import React, { useState } from 'react';
import { Globe, LeafyGreen, Squirrel, Pickaxe, Trees } from 'lucide-react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import PortfolioAreaChart from 'src/shared/components/chart-templates/portfolio-area-chart/PortfolioAreaReChart';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import HabitatMiniMap from 'src/app/maps/views/habitat-map/components/habitat-mini-map/HabitatMiniMap';
import { HabitatInsightsMetricType, habitatInsightsMetricTypeLabel } from 'src/shared/types/graph/survey';
import { getBiodiversityIntactnessClassification } from 'src/shared/helpers/metricHelpers';
import HabitatBaselinePicker from 'src/shared/components/habitat-baseline-picker/HabitatBaselinePicker';
import ComparisonPeriodToggle from 'src/shared/components/comparison-period-toggle/ComparisonPeriodToggle';
import TrendPill, { percentageTrendExample } from 'src/shared/components/trend-pill/TrendPill';
import Show from 'src/shared/components/show/Show';
import usePreviewRelease from 'src/shared/hooks/usePreviewRelease';
import {
    baselineYearFor,
    baselineYearsBefore,
    ComparisonPeriod,
    habitatYears,
    landCoverComparison,
} from 'src/shared/helpers/habitatComparison';
import { baselineFallback } from 'src/shared/helpers/baselineFallback';
import GaugeChart from 'src/shared/components/charts/GaugeChart';
import { PortfolioSiteMetric } from 'src/shared/types/graph/portfolio';
import { SiteSummaryData } from '../state/api/siteSummaryQuery';
import useSiteSummaryAnalytics from '../hooks/useSiteSummaryAnalytics';
import styles from '../SiteSummary.module.scss';

type Props = {
    site: SiteSummaryData;
};

const SiteHabitatHealth = ({ site }: Props) => {
    const navigate = useAppNavigation();
    const { trackExploreButtonClick, trackHabitatBaselineChange, trackLandCoverPeriodChange } = useSiteSummaryAnalytics();
    const [chosenBaseline, setChosenBaseline] = useState<number>();
    const [landCoverPeriod, setLandCoverPeriod] = useState<ComparisonPeriod>('current');
    const showHabitatComparison = usePreviewRelease('habitatComparison');

    const years = habitatYears(site.metrics);
    const latestYear = years[years.length - 1];

    const getSeries = (label: string) => {
        const metric = site.metrics.find(m => m.label === label);
        if (!metric) return undefined;
        if ('datum' in metric && metric.datum && metric.years) {
            return metric.years.map((year, i) => ({ year, value: metric.datum?.[i] || 0 }));
        }
        return undefined;
    };

    const landCoverMetric = site.metrics.find(m => m.label === habitatInsightsMetricTypeLabel.Land_Cover);
    const vegetationSeries = getSeries(habitatInsightsMetricTypeLabel.Vegetation_Condition);
    const connectivitySeries = getSeries(habitatInsightsMetricTypeLabel.All_Connectivity);
    const humanModificationMetric = site.metrics.find(
        m => m.name === HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX || m.label === 'Human modification'
    );

    const biodiversityMetric = site.metrics.find(m => m.name === HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX);

    const landCover = landCoverView(landCoverMetric, landCoverPeriod, chosenBaseline);

    // Biodiversity Intactness classification
    const biodiversityValue = Number(biodiversityMetric?.data || 0);
    const biodiversityYear = biodiversityMetric?.year;
    const intactnessClassification = getBiodiversityIntactnessClassification(biodiversityValue);

    const handleExploreButtonClick = (section: string) => {
        trackExploreButtonClick({ section: section });
        navigate.toHabitatTrends();
    };

    return (
        <div className='grid grid-cols-2 gap-3'>
            <div className='col-span-2 flex justify-between items-center'>
                <div className={`text-xl text-black ${styles.sectionTitle}`}>Habitat health indicators</div>
                <Show when={showHabitatComparison && years.length > 0}>
                    <HabitatBaselinePicker
                        baselineYear={baselineYearFor(years, latestYear, chosenBaseline)}
                        options={baselineYearsBefore(years, latestYear)}
                        onChange={year => {
                            setChosenBaseline(year);
                            trackHabitatBaselineChange(String(year));
                        }}
                    />
                </Show>
            </div>

            {landCover && (
                <SummaryCard
                    dataTestId='site-land-cover-card'
                    containerClasses={`gap-3 p-5 h-[300px] ${styles.habitatCardLandCover}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Globe className='text-primary' />
                        </div>
                    }
                    cardTitle='Land cover'
                    cardSubtitle='Distribution of land cover types across the site, highlighting dominant land cover and land uses.'
                    cardBody={
                        <div className='flex flex-col gap-2'>
                            <span className='text-xs text-grey-80' data-testid='site-land-cover-comparison-year'>
                                {landCover.comparisonYear}
                                {landCover.comparisonCoverage &&
                                    (landCover.comparisonCoverage.complete ? ' · Full year' : ' · Year to date')}
                            </span>
                            <div className='flex gap-6'>
                                {[
                                    { metricKey: 'Cumulative Vegetation Area', text: 'Combined vegetated area' },
                                    { metricKey: 'Trees', text: 'Tree cover' },
                                ].map(({ metricKey, text }) => (
                                    <div key={metricKey} className='flex flex-col gap-3'>
                                        <div className='flex flex-col'>
                                            <span className='text-[42px] text-grey-80 tabular-nums'>{landCover.valueOf(metricKey)}%</span>
                                            <span className='flex items-center text-sm text-grey-80 pl-1'>{text}</span>
                                        </div>
                                        <TrendPill
                                            trendValue={landCover.trendOf(metricKey)}
                                            baselineYear={landCover.baselineYear}
                                            comparisonYear={landCover.comparisonYear}
                                            example={percentageTrendExample(text)}
                                            fallback={landCover.fallback}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                    action={
                        <div className='flex gap-3 items-center'>
                            <Show when={showHabitatComparison && landCover.hasLastFullYear}>
                                <ComparisonPeriodToggle
                                    value={landCover.shownPeriod}
                                    onChange={period => {
                                        setLandCoverPeriod(period);
                                        trackLandCoverPeriodChange(period);
                                    }}
                                />
                            </Show>
                            <ExploreButton sectionName='land-cover' onClick={() => handleExploreButtonClick('land_cover')} />
                        </div>
                    }
                />
            )}

            <div className={`rounded-lg shadow-portfolio-summary-card overflow-hidden h-[300px] ${styles.mapCard}`}>
                <HabitatMiniMap />
            </div>

            {vegetationSeries && (
                <SummaryCard
                    dataTestId='site-vegetation-health-card'
                    containerClasses={`gap-3 p-5 h-[340px] ${styles.habitatCardVegetation}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <LeafyGreen className='text-primary' />
                        </div>
                    }
                    cardTitle='Vegetation health'
                    cardSubtitle='Assessment of plant vigor and density, crucial for ecosystem function.'
                    cardBody={<PortfolioAreaChart chartData={vegetationSeries} chartHeight={140} baselineYear={chosenBaseline} />}
                    action={<ExploreButton sectionName='vegetation-health' onClick={() => handleExploreButtonClick('vegetation_health')} />}
                />
            )}

            {connectivitySeries && (
                <SummaryCard
                    dataTestId='site-connectivity-card'
                    containerClasses={`gap-3 p-5 h-[340px] ${styles.habitatCardConnectivity}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Trees className='text-primary' />
                        </div>
                    }
                    cardTitle='Connectivity'
                    cardSubtitle='Degree to which natural habitats are linked, allowing species movement.'
                    cardBody={<PortfolioAreaChart chartData={connectivitySeries} chartHeight={140} baselineYear={chosenBaseline} />}
                    action={<ExploreButton sectionName='connectivity' onClick={() => handleExploreButtonClick('connectivity')} />}
                />
            )}

            {biodiversityMetric && biodiversityYear && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 ${styles.habitatCardBiodiversity}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Squirrel className='text-primary' />
                        </div>
                    }
                    cardTitle={
                        <span className='flex flex-col items-start'>
                            Biodiversity Intactness Index
                            <span className='text-sm text-grey-80'>For {biodiversityYear}</span>
                        </span>
                    }
                    cardSubtitle='Measures the remaining biodiversity relative to an undisturbed baseline.'
                    cardBody={
                        <div className='flex justify-center'>
                            <GaugeChart
                                value={biodiversityValue}
                                startLabel={
                                    <>
                                        Severely
                                        <br />
                                        degraded
                                    </>
                                }
                                endLabel={'Intact'}
                                color={intactnessClassification.color}
                                category={intactnessClassification.category}
                            />
                        </div>
                    }
                    action={
                        <ExploreButton
                            sectionName='biodiversity-intactness'
                            onClick={() => handleExploreButtonClick('biodiversity_intactness')}
                        />
                    }
                />
            )}

            {humanModificationMetric && (
                <SummaryCard
                    containerClasses={`gap-3 p-5 ${styles.habitatCardHuman}`}
                    cardIcon={
                        <div className={styles.iconGradient}>
                            <Pickaxe className='text-primary' />
                        </div>
                    }
                    cardTitle='Human modification index'
                    cardSubtitle='Index summarising anthropogenic pressure.'
                    cardBody={(() => {
                        let latest = 0;
                        if ('data' in humanModificationMetric && humanModificationMetric.data) {
                            latest = Number(humanModificationMetric.data || 0);
                        } else if ('datum' in humanModificationMetric && humanModificationMetric.datum && humanModificationMetric.years) {
                            latest = humanModificationMetric.datum[humanModificationMetric.datum.length - 1] || 0;
                        }
                        let category = 'Low modification';
                        let color: 'green' | 'orange' | 'red' = 'green';
                        if (latest > 0.1 && latest <= 0.4) {
                            category = 'Moderate modification';
                            color = 'orange';
                        } else if (latest > 0.4) {
                            category = latest > 0.7 ? 'Very high modification' : 'High modification';
                            color = 'red';
                        }
                        return (
                            <div className='flex justify-center'>
                                <GaugeChart
                                    value={latest}
                                    startLabel={'Natural'}
                                    endLabel={
                                        <>
                                            Very high
                                            <br />
                                            modification
                                        </>
                                    }
                                    color={color}
                                    category={category}
                                />
                            </div>
                        );
                    })()}
                    action={
                        <ExploreButton sectionName='human-modification' onClick={() => handleExploreButtonClick('human_modification')} />
                    }
                />
            )}
        </div>
    );
};

// The land cover years being compared and how to read a metric's value and trend
// for them, or nothing when the site has no land cover data.
const landCoverView = (metric: PortfolioSiteMetric | undefined, period: ComparisonPeriod, chosenBaseline: number | undefined) => {
    const trendData = (metric?.metricWithTrend ?? {}) as unknown as Record<string, Record<string, number>>;
    if (metric && Object.keys(trendData).length > 0) {
        const { years, coverage, comparisonYear, shownPeriod, hasLastFullYear } = landCoverComparison(metric, period);
        const baselineYear = baselineYearFor(years, comparisonYear, chosenBaseline);
        const valueOf = (metricKey: string) => trendData[comparisonYear][metricKey] || 0;
        const trendOf = (metricKey: string) => {
            const baselineValue = trendData[baselineYear][metricKey] || 0;
            return ((valueOf(metricKey) - baselineValue) / (baselineValue || 1)) * 100;
        };
        return {
            comparisonYear,
            comparisonCoverage: coverage.find(entry => entry.year === comparisonYear),
            shownPeriod,
            hasLastFullYear,
            baselineYear,
            fallback: baselineFallback(years, chosenBaseline, baselineYear),
            valueOf,
            trendOf,
        };
    } else {
        return undefined;
    }
};

export default SiteHabitatHealth;
